'use client';

import { useEffect, useRef, useState } from 'react';
import { buildShareLinks } from '@/lib/share-links';
import { SOCIAL_ICONS } from '@/components/social-icons';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

export interface ShareLinksProps {
  /** Canonical post URL built from NEXT_PUBLIC_SITE_URL — never window.location, so the shared link is the canonical host even behind a proxy or preview hostname. */
  url: string;
  title: string;
  slug: string;
  layout?: 'row' | 'column';
}

type CopyState = 'idle' | 'copied' | 'manual';

const COPIED_MS = 2000;

/** GA4 recommended `share` event — lets marketing see which platform readers use. */
function trackShare(method: string, slug: string) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', 'share', { method, content_type: 'article', item_id: slug });
  }
}

const BUTTON = 'inline-flex items-center justify-center w-10 h-10 rounded-full transition-opacity hover:opacity-80';
const BUTTON_STYLE = {
  backgroundColor: 'color-mix(in srgb, var(--color-t-primary) 8%, transparent)',
  color: 'var(--color-t-primary)',
};

/** What the always-mounted live region says per state — ONE channel, so nothing is announced twice. */
const STATUS_TEXT: Record<CopyState, string> = {
  idle: '',
  copied: 'Link copied',
  manual: 'Copy failed — the link is selected below, press Ctrl+C or Cmd+C',
};

export default function ShareLinks({ url, title, slug, layout = 'row' }: ShareLinksProps) {
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);
  const links = buildShareLinks(url, title);

  function clearReset() {
    if (resetTimer.current) clearTimeout(resetTimer.current);
    resetTimer.current = null;
  }

  // StrictMode (dev) runs mount → cleanup → mount, so the body must re-arm `mounted`.
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; clearReset(); };
  }, []);

  async function copy() {
    // The try covers ONLY the clipboard operation — a gtag throw must not
    // turn a successful copy into a "Copy failed".
    let copiedOk = false;
    try {
      if (typeof navigator.clipboard?.writeText !== 'function') throw new Error('clipboard unavailable');
      await navigator.clipboard.writeText(url);
      copiedOk = true;
    } catch {
      // http origins (no Clipboard API) and permission rejections on https.
    }

    // Count the share only once the copy actually happened — even if the
    // reader has since navigated away (the write did succeed).
    if (copiedOk) trackShare('copy_link', slug);
    // The reader may have navigated away while the write was pending.
    if (!mounted.current) return;

    // Clear at the STATE TRANSITION, not at click time: with two clicks in
    // flight, a timer armed by the first must not outlive the second's outcome.
    clearReset();
    if (!copiedOk) {
      // Hand the reader the URL to copy by hand.
      setCopyState('manual');
      return;
    }
    setCopyState('copied');
    resetTimer.current = setTimeout(() => { resetTimer.current = null; setCopyState('idle'); }, COPIED_MS);
  }

  return (
    <div
      data-testid="share-links"
      className={layout === 'column' ? 'flex flex-wrap items-center gap-3 lg:flex-col lg:items-start' : 'flex flex-wrap items-center gap-3'}
    >
      <span className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--color-t-heading)' }}>
        Share
      </span>
      <div className="flex flex-wrap items-center gap-2">
        {links.map((l) => (
          <a
            key={l.key}
            href={l.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={l.label}
            title={l.label}
            className={BUTTON}
            style={BUTTON_STYLE}
            onClick={() => trackShare(l.key, slug)}
          >
            {SOCIAL_ICONS[l.icon]}
          </a>
        ))}
        {/* Name and title stay stable — the live region below carries the state. */}
        <button type="button" onClick={copy} aria-label="Copy link" title="Copy link" className={BUTTON} style={BUTTON_STYLE}>
          {copyState === 'copied' ? SOCIAL_ICONS.Check : SOCIAL_ICONS.Link}
        </button>
        {copyState === 'copied' && (
          <span aria-hidden="true" className="text-xs font-medium" style={{ color: 'var(--color-t-body)' }}>
            Copied
          </span>
        )}
      </div>
      {/* Always mounted: screen readers announce CHANGES to a live region, not regions that appear pre-filled. */}
      <span role="status" aria-live="polite" className="sr-only">
        {STATUS_TEXT[copyState]}
      </span>
      {copyState === 'manual' && (
        <input
          readOnly
          autoFocus
          value={url}
          aria-label="Post URL"
          onFocus={(e) => e.currentTarget.select()}
          className="w-full text-xs px-2 py-1.5 rounded border bg-transparent"
          style={{ borderColor: 'color-mix(in srgb, var(--color-t-primary) 20%, transparent)', color: 'var(--color-t-body)' }}
        />
      )}
    </div>
  );
}
